import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {
  Box, ButtonBase, IconButton, InputBase, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tooltip, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {EyeIcon, GitCompareIcon, Refresh01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {useSetAtom} from "jotai";
import {toast} from "react-toastify";
import {useRegistryProject} from "../RegistryProjectContext";
import {useSubProjectFlows} from "../useSubProjectFlows";
import {MessageAnnotationsRepository} from "../../../repositories/MessageAnnotationRepository";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {useDebounce} from "../../../hooks/useDebounce";
import DateRangePicker from "../../../components/date-range/DateRangePicker";
import {SelectedConversationAtom} from "../../project/dashboard/atoms";
import {controlSx, Panel, PanelHeading, PillSelect} from "../dashboard/components/Panel";
import {csvFileName, downloadBlob, ExportButton} from "../dashboard/components/ExportButton";
import {headLabelSx} from "../dashboard/components/HeaderFilters";
import {ConversationModal} from "../dashboard/components/ConversationModal";
import {StatStrip} from "../dashboard/components/StatTile";
import {AnnotationRow, cellSx} from "./AnnotationRow";
import {subProjectLabel} from "../../../utils/subProjects";

const DATE_TIME = "YYYY-MM-DDTHH:mm:ss";

const STATUS_OPTIONS = [
  {value: "", label: "All annotations"},
  {value: "false", label: "Unresolved"},
  {value: "true", label: "Resolved"},
];

const SORT_OPTIONS = [
  {value: "dateCreated:DESC", label: "Newest first"},
  {value: "dateCreated:ASC", label: "Oldest first"},
  {value: "cerScore:DESC", label: "Highest CER"},
  {value: "werScore:DESC", label: "Highest WER"},
];

const headCellSx = {
  height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};

// Normal / Diff segmented toggle
const ViewToggle = ({value, onChange}) => (
  <Stack direction={"row"} sx={{p: 0.5, gap: 0.5, borderRadius: "12px", border: "1px solid #E4E4EE", bgcolor: "#fff"}}>
    {[["normal", "Normal", EyeIcon], ["diff", "Diff", GitCompareIcon]].map(([key, label, icon]) => (
      <ButtonBase key={key} onClick={() => onChange(key)} sx={{
        height: 30, px: 1.5, gap: 0.75, borderRadius: "9px", fontSize: 13, fontWeight: 500,
        color: value === key ? "#4B55C9" : "#6B6B80",
        bgcolor: value === key ? "rgba(132, 145, 247, 0.12)" : "transparent",
        "&:hover": {bgcolor: value === key ? "rgba(132, 145, 247, 0.18)" : "#F4F4FA"},
      }}>
        <HugeiconsIcon icon={icon} size={15}/>
        {label}
      </ButtonBase>
    ))}
  </Stack>
);

// Speech-to-text corrections of the project's conversations: statistics, then the annotations (paged), each editable
// and comparable to its transcription.
export const RegistryMessageAnnotationsPage = () => {
  const {project} = useRegistryProject();
  const lookups = useSubProjectFlows(project);
  const setSelectedConversation = useSetAtom(SelectedConversationAtom);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // filters ("" = not applied)
  const [subProjectId, setSubProjectId] = useState("");
  const [resolved, setResolved] = useState("");
  const [conversationSearch, setConversationSearch] = useState("");
  const [range, setRange] = useState({start: null, end: null});
  const conversationDebounced = useDebounce(conversationSearch, 400);
  const [sort, setSort] = useState({field: "dateCreated", direction: "DESC"});
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [viewMode, setViewMode] = useState("normal");

  const [annotations, setAnnotations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const requestRef = useRef(0);

  const dateFilters = useMemo(() => ({
    startDate: range.start ? moment(range.start).format(DATE_TIME) : undefined,
    endDate: range.end ? moment(range.end).format(DATE_TIME) : undefined,
  }), [range]);

  const conversationId = /^\d+$/.test(conversationDebounced.trim().replace(/^#/, ""))
    ? conversationDebounced.trim().replace(/^#/, "")
    : undefined;

  const loadAnnotations = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const response = await MessageAnnotationsRepository.searchProject({
        projectId: String(project.id),
        subProjectId,
        resolved,
        conversationId,
        ...dateFilters,
        page,
        size,
        sort: sort.field,
        direction: sort.direction,
      });
      if (requestId !== requestRef.current) return;
      setAnnotations(response?.data?.content || []);
      setTotal(response?.data?.totalElements || 0);
    } catch (e) {
      console.error(e);
      if (requestId === requestRef.current) toast.error("Failed to load annotations");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [project.id, subProjectId, resolved, conversationId, dateFilters, page, size, sort]);

  const loadStatistics = useCallback(() => {
    MessageAnnotationsRepository.getProjectStatistics(String(project.id), subProjectId)
      .then(response => setStatistics(response?.data || {}))
      .catch(e => console.error("Failed to load annotation statistics:", e));
  }, [project.id, subProjectId]);

  useEffect(() => {
    loadAnnotations();
  }, [loadAnnotations]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // a new filter or sort starts on the first page
  useEffect(() => {
    setPage(0);
  }, [subProjectId, resolved, conversationId, dateFilters, sort, size]);

  const refresh = () => {
    loadAnnotations();
    loadStatistics();
  };

  const handleUpdated = (updated) => {
    if (!updated) return;
    setAnnotations(list => list.map(item => item.id === updated.id ? {...item, ...updated} : item));
    loadStatistics();
  };

  const toggleSort = (field) => setSort(current => ({
    field,
    direction: current.field === field && current.direction === "DESC" ? "ASC" : "DESC",
  }));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await MessageAnnotationsRepository.exportProject({
        projectId: String(project.id), subProjectId, ...dateFilters,
      });
      const disposition = response?.headers?.["content-disposition"] || "";
      downloadBlob(response.data, disposition.match(/filename="?([^";]+)"?/)?.[1] || csvFileName("message-annotations"));
    } catch (e) {
      console.error(e);
      toast.error("Failed to export annotations");
    } finally {
      setExporting(false);
    }
  };

  // Opens the conversation in the modal, scrolled to the annotated message
  const openConversation = (annotation) => {
    setSelectedConversation({id: annotation.conversationId, flowUid: annotation.flowUid, messageId: annotation.messageId});
    ConversationRepository.getConversation({id: annotation.conversationId, flowUid: annotation.flowUid})
      .then(response => setSelectedConversation(current => current?.id === annotation.conversationId
        ? {...(response?.data || {}), messageId: annotation.messageId}
        : current))
      .catch(e => console.error(e));
  };

  const languageOptions = useMemo(() => [
    {value: "", label: "All sub projects"},
    ...project.subProjects.map(item => ({value: String(item.id), label: subProjectLabel(item)})),
  ], [project.subProjects]);

  const subProjectOf = (annotation) => project.subProjects.find(item =>
    String(item.id) === String(annotation.subProjectId) || (annotation.flowUid && item.flowUid === annotation.flowUid));

  const totalAnnotations = Number(statistics?.totalAnnotations) || 0;
  const totalResolved = Number(statistics?.totalResolved) || 0;
  const formatScore = (value) => value === null || value === undefined ? "—" : Number(value).toFixed(2);

  const sortableHead = (field, label) => (
    <TableSortLabel
      active={sort.field === field}
      direction={sort.field === field ? sort.direction.toLowerCase() : "desc"}
      onClick={() => toggleSort(field)}
      sx={{"&, &:hover, &.Mui-active": {color: sort.field === field ? "#4B55C9" : "#8A8AA0"}, "& .MuiTableSortLabel-icon": {color: "inherit !important"}}}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: {xs: "100%", md: "calc(100% - 128px)"}, width: "100%", mx: "auto", boxSizing: "border-box"}}>
      {/* Heading */}
      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>SPEECH QUALITY</Typography>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
            Message annotations
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
            Review, compare and resolve corrections of speech-to-text transcriptions
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
          <PillSelect value={subProjectId} options={languageOptions} onChange={setSubProjectId}
                      placeholder={"Sub project"} ariaLabel={"Filter by sub project"}/>
          <PillSelect value={resolved} options={STATUS_OPTIONS} onChange={setResolved}
                      placeholder={"Status"} ariaLabel={"Filter by status"}/>
          {isMobile && (
            <PillSelect value={`${sort.field}:${sort.direction}`} options={SORT_OPTIONS} ariaLabel={"Sort annotations"}
                        onChange={(value) => { const [field, direction] = value.split(":"); setSort({field, direction}); }}/>
          )}
          <ViewToggle value={viewMode} onChange={setViewMode}/>
        </Stack>
      </Stack>

      {/* Statistics */}
      <StatStrip items={[
        {label: "Annotations", value: statistics ? totalAnnotations.toLocaleString() : "–"},
        {
          label: "Resolved",
          value: statistics ? `${totalAnnotations ? Math.round((totalResolved / totalAnnotations) * 100) : 0}%` : "–",
          note: statistics ? `${totalResolved.toLocaleString()} of ${totalAnnotations.toLocaleString()}` : null,
          meter: statistics && totalAnnotations ? totalResolved / totalAnnotations : 0,
        },
        {label: "Average CER", value: formatScore(statistics?.avgCerScore), note: "Lower is better · errors per character"},
        {label: "Average WER", value: formatScore(statistics?.avgWerScore), note: "Lower is better · errors per word"},
      ]}/>

      {/* Annotations */}
      <Panel sx={{overflow: "hidden"}}>
        <Box sx={{p: {xs: 2, md: 2.5}, pb: 2}}>
          <PanelHeading
            stackOnMobile
            kicker={"CORRECTIONS"}
            title={"Annotations"}
            count={total}
            description={"Original transcription next to its correction. Edit, compare with Diff, or open the conversation."}
            actions={<>
              <Box component={"label"} sx={{...controlSx, width: {xs: "100%", md: 180}}}>
                <HugeiconsIcon icon={Search01Icon} size={16}/>
                <InputBase
                  placeholder={"Conversation #id"}
                  value={conversationSearch}
                  onChange={(e) => setConversationSearch(e.target.value)}
                  inputProps={{"aria-label": "Filter by conversation id"}}
                  sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                />
              </Box>
              <Box sx={{...controlSx, px: 0.5}}>
                <DateRangePicker
                  placeholder={"Date range"}
                  dateFormat={"DD MMM"}
                  startDate={range.start}
                  endDate={range.end}
                  onStartChange={(date) => setRange(current => ({...current, start: date}))}
                  onEndChange={(date) => setRange(current => ({...current, end: date}))}
                  triggerSx={{
                    ...headLabelSx(Boolean(range.start && range.end)), mx: 0, height: 28, fontSize: 13,
                    letterSpacing: 0, fontWeight: 500, textTransform: "none", "& svg": {width: 15, height: 15},
                  }}
                />
              </Box>
              <Tooltip title={"Refresh"}>
                <IconButton onClick={refresh} sx={{borderRadius: "50% !important", color: "#8A8AA0"}}>
                  <HugeiconsIcon icon={Refresh01Icon} size={17}/>
                </IconButton>
              </Tooltip>
              <ExportButton title={"Export annotations (CSV)"} loading={exporting} onClick={exportCsv}/>
            </>}
          />
        </Box>

        {isMobile ? (
          <Stack gap={1.5} sx={{px: 2, pb: 2}}>
            {loading && annotations.length === 0 && Array.from({length: 3}).map((_, index) => (
              <Skeleton key={index} variant={"rounded"} height={150} sx={{borderRadius: "14px"}}/>
            ))}
            {annotations.map(annotation => (
              <AnnotationRow
                key={annotation.id}
                mobile
                annotation={annotation}
                language={subProjectOf(annotation)}
                diff={viewMode === "diff"}
                onUpdated={handleUpdated}
                onDeleted={() => refresh()}
                onOpenConversation={openConversation}
              />
            ))}
            {!loading && annotations.length === 0 && (
              <Typography sx={{py: 4, textAlign: "center", fontSize: 13, color: "#8A8AA0"}}>
                No annotations match these filters
              </Typography>
            )}
          </Stack>
        ) : (
        <TableContainer sx={{overflowX: "auto"}}>
          <Table size={"small"} sx={{minWidth: 980}}>
            <TableHead>
              <TableRow>
                <TableCell sx={{...headCellSx, pl: 2.5}}>TRANSCRIPTION</TableCell>
                <TableCell sx={headCellSx}>{viewMode === "diff" ? "DIFFERENCE" : "CORRECTION"}</TableCell>
                <TableCell sx={headCellSx}>LANGUAGE</TableCell>
                <TableCell sx={headCellSx}>{sortableHead("cerScore", "CER")}</TableCell>
                <TableCell sx={headCellSx}>{sortableHead("werScore", "WER")}</TableCell>
                <TableCell sx={headCellSx}>AUDIO</TableCell>
                <TableCell sx={headCellSx}>{sortableHead("dateCreated", "DATE")}</TableCell>
                <TableCell sx={{...headCellSx, pr: 2.5}}/>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && annotations.length === 0 && Array.from({length: 5}).map((_, index) => (
                <TableRow key={index}><TableCell colSpan={8} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell></TableRow>
              ))}
              {annotations.map(annotation => (
                <AnnotationRow
                  key={annotation.id}
                  annotation={annotation}
                  language={subProjectOf(annotation)}
                  diff={viewMode === "diff"}
                  onUpdated={handleUpdated}
                  onDeleted={() => refresh()}
                  onOpenConversation={openConversation}
                />
              ))}
              {!loading && annotations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                    No annotations match these filters
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        <TablePagination
          component={"div"}
          count={total}
          page={page}
          onPageChange={(e, next) => setPage(next)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => setSize(Number(e.target.value))}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            borderTop: "1px solid #ECECF3", color: "#6B6B80",
            "& .MuiTablePagination-toolbar": {px: {xs: 1, md: 2}, flexWrap: "wrap", justifyContent: {xs: "center", md: "flex-end"}},
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": {display: {xs: "none", sm: "block"}},
            "& .MuiTablePagination-spacer": {display: {xs: "none", md: "block"}},
          }}
        />
      </Panel>

      <ConversationModal
        flowOf={lookups.flowOf}
        subProjectOf={lookups.subProjectOf}
        tagsOf={lookups.tagsOf}
      />
    </Stack>
  );
};
